// Auth context — wraps /api/auth/me. The single client-side identity source; mirrors the
// server's getCurrentUser() (ADR-008). Provides the dev role-picker + logout.
import { createContext, useContext, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { CurrentUser } from "@rrid/shared-types";
import { api, ApiError } from "./api.js";
import { qk } from "./queryKeys.js";

interface AuthValue {
  user: CurrentUser | null;
  loading: boolean;
  devLogin: (email: string, snapshot?: string) => Promise<void>;
  switchRole: (email: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({
    queryKey: qk.me,
    queryFn: async () => {
      try {
        return await api.get<CurrentUser>("/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    retry: false,
    staleTime: 60_000,
  });

  const value: AuthValue = {
    user: data ?? null,
    loading: isLoading,
    devLogin: async (email, snapshot) => {
      await api.post("/auth/dev-login", { email, snapshot });
      qc.clear();
      await qc.invalidateQueries({ queryKey: qk.me });
      navigate("/", { replace: true });
    },
    switchRole: async (email) => {
      await api.post("/auth/dev-login", { email });
      // FR-L1: update the identity IN PLACE. `qc.clear()` would wipe the `me` query, drop App
      // into its full-screen loading remount, and reset the current portal to Home. Invalidating
      // (without clearing) refetches `me` + every mounted data query under the new role while the
      // current route stays put. Only devLogin/logout navigate.
      await qc.invalidateQueries();
    },
    logout: async () => {
      await api.post("/auth/logout");
      qc.clear();
      await qc.invalidateQueries({ queryKey: qk.me });
      navigate("/login", { replace: true });
    },
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
